<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const PERMISSION_GROUPS = [
  {
    key: 'benefit360',
    icon: 'i-lucide-user-search',
    permissions: [
      'benefit360_query',
      'benefit360_reveal',
      'benefit360_admin',
      'benefit360_campaign_download',
    ],
  },
  {
    key: 'conversations',
    icon: 'i-lucide-messages-square',
    permissions: [
      'conversation_manage',
      'conversation_clear',
      'conversation_unassigned_manage',
      'conversation_participating_manage',
    ],
  },
  {
    key: 'people',
    icon: 'i-lucide-users-round',
    permissions: [
      'contact_manage',
      'label_manage',
      'agent_manage',
      'team_manage',
      'assignment_policy_manage',
    ],
  },
  {
    key: 'service',
    icon: 'i-lucide-headphones',
    permissions: [
      'inbox_manage',
      'canned_response_manage',
      'macro_manage',
      'sla_manage',
      'agent_bot_manage',
    ],
  },
  {
    key: 'automation',
    icon: 'i-lucide-workflow',
    permissions: ['automation_manage', 'integration_manage', 'webhook_manage'],
  },
  {
    key: 'content',
    icon: 'i-lucide-megaphone',
    permissions: [
      'campaign_manage',
      'custom_attribute_manage',
      'knowledge_base_manage',
    ],
  },
  {
    key: 'intelligence',
    icon: 'i-lucide-chart-spline',
    permissions: ['report_manage', 'inventory_manage', 'openai_manage'],
  },
  {
    key: 'finance',
    icon: 'i-hugeicons-wallet-01',
    permissions: ['finance_manage', 'billing_manage', 'pix_charge_create'],
  },
];

const PRESET_PERMISSIONS = {
  full: AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  service: [
    'conversation_manage',
    'conversation_unassigned_manage',
    'conversation_participating_manage',
    'contact_manage',
    'label_manage',
    'inbox_manage',
    'canned_response_manage',
    'macro_manage',
  ],
  team: [
    'agent_manage',
    'team_manage',
    'assignment_policy_manage',
    'report_manage',
  ],
  clear: [],
};

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();
const availablePermissions = computed(() =>
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS.filter(
    permission =>
      !permission.startsWith('benefit360_') ||
      !!currentAccount.value?.features?.talkvia_corban_benefit360
  )
);

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);
const permissionSearch = ref('');

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));
const normalizedPermissionSearch = computed(() =>
  permissionSearch.value.trim().toLocaleLowerCase()
);
const visiblePermissionGroups = computed(() =>
  PERMISSION_GROUPS.map(group => ({
    ...group,
    permissions: group.permissions.filter(
      permission =>
        availablePermissions.value.includes(permission) &&
        t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`)
          .toLocaleLowerCase()
          .includes(normalizedPermissionSearch.value)
    ),
  })).filter(group => group.permissions.length)
);
const presetOptions = computed(() =>
  Object.keys(PRESET_PERMISSIONS).map(key => ({
    key,
    label: t(`CUSTOM_ROLE.FORM.PRESETS.${key.toUpperCase()}`),
  }))
);

const applyPreset = key => {
  selectedPermissions.value = PRESET_PERMISSIONS[key].filter(permission =>
    availablePermissions.value.includes(permission)
  );
};

const selectedInGroup = group =>
  group.permissions.filter(permission =>
    selectedPermissions.value.includes(permission)
  ).length;

const isGroupSelected = group =>
  group.permissions.every(permission =>
    selectedPermissions.value.includes(permission)
  );

const toggleGroup = group => {
  const groupPermissions = new Set(group.permissions);
  if (isGroupSelected(group)) {
    selectedPermissions.value = selectedPermissions.value.filter(
      permission => !groupPermissions.has(permission)
    );
    return;
  }

  selectedPermissions.value = [
    ...new Set([...selectedPermissions.value, ...group.permissions]),
  ];
};

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <div
    class="custom-role-modal flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden bg-n-solid-1"
  >
    <header class="border-b border-n-weak px-6 pb-5 pt-6 md:px-8">
      <div class="flex items-start gap-3 pr-8">
        <span
          class="grid size-11 shrink-0 place-content-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon icon="i-lucide-shield-check" class="size-5" />
        </span>
        <div>
          <h2 class="m-0 text-xl font-semibold text-n-slate-12">
            {{ modalTitle }}
          </h2>
          <p class="mb-0 mt-1 max-w-2xl text-sm leading-5 text-n-slate-10">
            {{ modalDescription }}
          </p>
        </div>
      </div>
    </header>

    <form
      class="custom-role-form flex min-h-0 flex-1 flex-col"
      @submit.prevent="handleCustomRole"
    >
      <div class="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6 md:px-8">
        <section>
          <div class="mb-3 flex items-center gap-2">
            <span
              class="grid size-7 place-content-center rounded-lg bg-n-alpha-2"
            >
              <Icon icon="i-lucide-id-card" class="size-4 text-n-slate-10" />
            </span>
            <div>
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                {{ $t('CUSTOM_ROLE.FORM.IDENTITY_TITLE') }}
              </h3>
              <p class="m-0 text-xs text-n-slate-9">
                {{ $t('CUSTOM_ROLE.FORM.IDENTITY_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="grid gap-4 md:grid-cols-2">
            <label
              class="m-0 grid gap-1.5 text-sm font-medium text-n-slate-12"
              :class="{ error: v$.name.$error }"
            >
              {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
              <input
                ref="nameInput"
                v-model.trim="name"
                type="text"
                :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
                class="m-0 h-11 rounded-xl border border-n-weak bg-n-alpha-1 px-3 text-sm"
                @blur="v$.name.$touch"
              />
            </label>
            <label
              class="m-0 grid gap-1.5 text-sm font-medium text-n-slate-12"
              :class="{ error: v$.description.$error }"
            >
              {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}
              <textarea
                v-model="description"
                :rows="2"
                :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
                class="m-0 min-h-11 resize-y rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm"
                @blur="v$.description.$touch"
              />
            </label>
          </div>
        </section>

        <section>
          <div class="mb-3">
            <div>
              <h3
                class="m-0 text-sm font-semibold"
                :class="
                  v$.selectedPermissions.$error
                    ? 'text-n-ruby-9'
                    : 'text-n-slate-12'
                "
              >
                {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
              </h3>
              <p class="m-0 mt-0.5 text-xs text-n-slate-9">
                {{
                  $t('CUSTOM_ROLE.FORM.SELECTED_COUNT', {
                    selected: selectedPermissions.length,
                    total: availablePermissions.length,
                  })
                }}
              </p>
            </div>
          </div>

          <div
            class="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          >
            <div
              class="group relative flex w-full min-w-0 items-center lg:w-80"
            >
              <Icon
                icon="i-lucide-search"
                class="pointer-events-none absolute left-3 top-1/2 z-10 size-3.5 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand"
              />
              <Input
                v-model="permissionSearch"
                type="search"
                :placeholder="$t('CUSTOM_ROLE.FORM.SEARCH_PLACEHOLDER')"
                class="w-full min-w-0"
                custom-input-class="!pl-9 !rounded-[0.625rem]"
              />
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="preset in presetOptions"
                :key="preset.key"
                type="button"
                class="rounded-full border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-xs font-medium text-n-slate-11 transition hover:border-n-brand/40 hover:bg-n-brand/10 hover:text-n-brand"
                @click="applyPreset(preset.key)"
              >
                {{ preset.label }}
              </button>
            </div>
          </div>

          <div class="grid gap-3 lg:grid-cols-2">
            <article
              v-for="group in visiblePermissionGroups"
              :key="group.key"
              class="overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
            >
              <button
                type="button"
                class="flex w-full items-center gap-2 border-b border-n-weak px-3.5 py-3 text-left transition hover:bg-n-alpha-2"
                @click="toggleGroup(group)"
              >
                <span
                  class="grid size-8 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-10"
                >
                  <Icon :icon="group.icon" class="size-4" />
                </span>
                <span class="min-w-0 flex-1">
                  <strong class="block text-sm font-semibold text-n-slate-12">
                    {{
                      $t(`CUSTOM_ROLE.FORM.GROUPS.${group.key.toUpperCase()}`)
                    }}
                  </strong>
                  <small class="text-[11px] text-n-slate-9">
                    {{ selectedInGroup(group) }}/{{ group.permissions.length }}
                    {{ $t('CUSTOM_ROLE.FORM.GROUP_SELECTED') }}
                  </small>
                </span>
                <span
                  class="grid size-5 place-content-center rounded-md border"
                  :class="
                    isGroupSelected(group)
                      ? 'border-n-brand bg-n-brand text-white'
                      : 'border-n-strong text-transparent'
                  "
                >
                  <Icon icon="i-lucide-check" class="size-3.5" />
                </span>
              </button>
              <div class="grid gap-1 p-2">
                <label
                  v-for="permission in group.permissions"
                  :key="permission"
                  :for="`custom-role-${permission}`"
                  class="m-0 flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-2 text-sm font-normal leading-5 text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
                >
                  <input
                    :id="`custom-role-${permission}`"
                    v-model="selectedPermissions"
                    type="checkbox"
                    :value="permission"
                    name="permissions"
                    class="mt-0.5 size-4 shrink-0 accent-n-brand"
                  />
                  <span>
                    {{
                      $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`)
                    }}
                  </span>
                </label>
              </div>
            </article>
          </div>

          <div
            v-if="!visiblePermissionGroups.length"
            class="rounded-xl border border-dashed border-n-weak px-4 py-8 text-center text-sm text-n-slate-9"
          >
            {{ $t('CUSTOM_ROLE.FORM.NO_PERMISSION_RESULTS') }}
          </div>
        </section>
      </div>

      <footer
        class="flex shrink-0 items-center justify-between gap-3 border-t border-n-weak bg-n-solid-1 px-6 py-4 md:px-8"
      >
        <span class="hidden text-xs text-n-slate-9 sm:block">
          {{ $t('CUSTOM_ROLE.FORM.REQUIRED_HINT') }}
        </span>
        <div class="ml-auto flex gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="submitButtonText"
            :disabled="isSubmitDisabled"
            :is-loading="addCustomRole.showLoading"
          />
        </div>
      </footer>
    </form>
  </div>
</template>

<style scoped>
.custom-role-form {
  padding: 0 !important;
}
</style>
